import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PaymentOversightPanel = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  const transactions = [
    {
      id: "TXN2025001",
      bookingId: "SLB2025001",
      buyer: "Rajesh Kumar Sharma",
      amount: 245000,
      type: "escrow_deposit",
      status: "completed",
      date: "2025-11-30",
      escrowBalance: 245000,
      releaseStatus: "pending"
    },
    {
      id: "TXN2025002",
      bookingId: "SLB2025002",
      buyer: "Priya Patel",
      amount: 196000,
      type: "escrow_release",
      status: "processing",
      date: "2025-11-29",
      escrowBalance: 0,
      releaseStatus: "80% released to driver"
    },
    {
      id: "TXN2025003",
      bookingId: "SLB2025003",
      buyer: "Amit Singh",
      amount: 150000,
      type: "dispute",
      status: "disputed",
      date: "2025-11-28",
      escrowBalance: 150000,
      disputeReason: "Quality mismatch - buyer claims grade S-30 received instead of M-30",
      disputeDate: "2025-11-29"
    },
    {
      id: "TXN2025004",
      bookingId: "SLB2025004",
      buyer: "Sunita Deshmukh",
      amount: 320000,
      type: "invoice_discount",
      status: "completed",
      date: "2025-11-27",
      advanceAmount: 272000,
      advancePercentage: 85
    }
  ];

  const disputes = [
    {
      id: "DSP2025001",
      transactionId: "TXN2025003",
      bookingId: "SLB2025003",
      buyer: "Amit Singh",
      seller: "Maharashtra Sugar Mills",
      amount: 150000,
      reason: "Quality mismatch - buyer claims grade S-30 received instead of M-30",
      filedDate: "2025-11-29",
      status: "under_review",
      priority: "high",
      assignedTo: "Admin Team A"
    },
    {
      id: "DSP2025002",
      transactionId: "TXN2025005",
      bookingId: "SLB2025005",
      buyer: "Vikram Reddy",
      seller: "Karnataka Sugar Factory",
      amount: 180000,
      reason: "Delivery delay - shipment arrived 5 days late causing storage issues",
      filedDate: "2025-11-28",
      status: "resolved",
      priority: "medium",
      resolution: "Partial refund of ₹18,000 (10%) issued to buyer for delay compensation",
      resolvedDate: "2025-11-30"
    }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'disputed', label: 'Disputed' }
  ];

  const filteredTransactions = transactions?.filter(t => 
    selectedFilter === 'all' ? true : t?.status === selectedFilter
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'processing': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'disputed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'under_review': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'escrow_deposit': return 'ArrowDownCircle';
      case 'escrow_release': return 'ArrowUpCircle';
      case 'dispute': return 'AlertTriangle';
      case 'invoice_discount': return 'Receipt';
      default: return 'DollarSign';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Payment Oversight</h2>
              <p className="text-sm text-muted-foreground">Monitor payment transactions and disputes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={filterOptions}
              value={selectedFilter}
              onChange={setSelectedFilter}
              placeholder="Filter by status"
              className="w-48"
            />
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export Report
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-700 dark:text-blue-400">Total Secured</span>
              <Icon name="Wallet" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{formatCurrency(545000)}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-700 dark:text-green-400">Released Today</span>
              <Icon name="TrendingUp" size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-300">{formatCurrency(196000)}</p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-red-700 dark:text-red-400">In Dispute</span>
              <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-900 dark:text-red-300">{formatCurrency(150000)}</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-700 dark:text-purple-400">Pending Release</span>
              <Icon name="Clock" size={20} className="text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">{formatCurrency(245000)}</p>
          </div>
        </div>

        <div className="space-y-3">
          {filteredTransactions?.map((transaction) => (
            <div key={transaction?.id} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={getTypeIcon(transaction?.type)} size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{transaction?.buyer}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)}`}>
                        {transaction?.status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Icon name="Hash" size={14} />
                        {transaction?.id}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Package" size={14} />
                        {transaction?.bookingId}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {new Date(transaction.date)?.toLocaleDateString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-xs text-muted-foreground">Amount</p>
                        <p className="text-lg font-bold text-foreground">{formatCurrency(transaction?.amount)}</p>
                      </div>
                      {transaction?.escrowBalance > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground">Secured Balance</p>
                          <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(transaction?.escrowBalance)}</p>
                        </div>
                      )}
                      {transaction?.releaseStatus && (
                        <div>
                          <p className="text-xs text-muted-foreground">Release Status</p>
                          <p className="text-sm font-medium text-foreground">{transaction?.releaseStatus}</p>
                        </div>
                      )}
                    </div>
                    {transaction?.disputeReason && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <p className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">Dispute Reason</p>
                        <p className="text-sm text-red-700 dark:text-red-300">{transaction?.disputeReason}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="Eye">
                    View Details
                  </Button>
                  {transaction?.status === 'disputed' && (
                    <Button variant="default" size="sm" iconName="Gavel">
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Active Disputes</h2>
              <p className="text-sm text-muted-foreground">Manage and resolve payment disputes</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {disputes?.map((dispute) => (
            <div key={dispute?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <Icon name="Gavel" size={20} className="text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{dispute?.id}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(dispute?.status)}`}>
                        {dispute?.status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        dispute?.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {dispute?.priority?.toUpperCase()} PRIORITY
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span>Buyer: {dispute?.buyer}</span>
                      <span>•</span>
                      <span>Seller: {dispute?.seller}</span>
                      <span>•</span>
                      <span>Amount: {formatCurrency(dispute?.amount)}</span>
                    </div>
                    <p className="text-sm text-foreground mb-2">{dispute?.reason}</p>
                    {dispute?.resolution && (
                      <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                        <p className="text-sm font-medium text-green-800 dark:text-green-400 mb-1">Resolution</p>
                        <p className="text-sm text-green-700 dark:text-green-300">{dispute?.resolution}</p>
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">Resolved on {new Date(dispute.resolvedDate)?.toLocaleDateString('en-IN')}</p>
                      </div>
                    )}
                  </div>
                </div>
                {dispute?.status === 'under_review' && (
                  <Button variant="default" size="sm" iconName="MessageSquare">
                    Take Action
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentOversightPanel;